<template>
  <div class="min-h-[1200px]">
    <main>
      <section class="pt-16 pb-24 lg:pt-[110px] lg:pb-[174px]">
        <div class="mx-auto max-w-6xl px-4">
          <h1 class="landing_h1 mb-12 text-center">
            <Translate translate-key="page_data.testimonials.h1" />
          </h1>
          <ul
            v-if="testimonialsData.length"
            class="mx-auto grid list-none grid-cols-1 gap-8 p-0 md:max-w-[550px] md:grid-cols-2 lg:max-w-none lg:grid-cols-2"
          >
            <li
              v-for="testimonial in testimonialsData"
              :key="testimonial.id"
              class="break-inside-avoid"
            >
              <TestimonialCard :testimonial="testimonial" />
            </li>
          </ul>
        </div>
      </section>
    </main>
  </div>
</template>
<script setup lang="ts">
import Translate from "~/shared/components/i18n/Translate/Translate.vue";
import TestimonialCard from "~/slices/testimonials/components/TestimonialCard.vue";
import type { TestimonialItem } from "~/slices/testimonials/types";
import { useI18n } from "vue-i18n";
import { useAuthFetch } from "~/shared/composables/useAuthFetch";

definePageMeta({ layout: "public" });

const { t } = useI18n();
const config = useRuntimeConfig();
const { authFetch } = useAuthFetch();

// ISR: server fetches reviews when rendering; payload is hydrated on the client.
// getCachedData ensures client uses server payload at hydration so v-if matches (avoids hydration mismatch).
const testimonialsUrl = `${config.public.baseURL}v1/testimonials/?type=1&limit=100`;
const { data: testimonialsRaw } = await useAsyncData(
  "testimonials-page",
  () =>
    authFetch<TestimonialItem[] | { results: TestimonialItem[] }>(
      testimonialsUrl,
    )
      .then((d) => (Array.isArray(d) ? d : (d?.results ?? [])))
      .catch((err) => {
        if (import.meta.dev) {
          console.warn("[testimonials] API request failed:", err);
        }
        return [] as TestimonialItem[];
      }),
  {
    // Nuxt 4: match default cache lookup (payload when hydrating, static otherwise)
    getCachedData: (key, nuxtApp) => {
      const data = nuxtApp.isHydrating
        ? nuxtApp.payload.data
        : (nuxtApp as { static?: { data?: Record<string, unknown> } }).static
            ?.data;
      return (
        data as Record<
          string,
          TestimonialItem[] | { results: TestimonialItem[] } | undefined
        >
      )?.[key];
    },
  },
);

const testimonialsData = computed(() => {
  const d = testimonialsRaw.value;
  if (Array.isArray(d)) return d;
  if (
    d &&
    typeof d === "object" &&
    "results" in d &&
    Array.isArray((d as { results: TestimonialItem[] }).results)
  ) {
    return (d as { results: TestimonialItem[] }).results;
  }
  return [];
});

const route = useRoute();
const siteUrl = (config.public.siteUrl as string) || "";

useSeoMeta({
  title: () => t("page_data.testimonials.seo_title"),
  description: () => t("page_data.testimonials.seo_description"),
  ogTitle: () => t("page_data.testimonials.seo_title"),
  ogDescription: () => t("page_data.testimonials.seo_description"),
  ogUrl: () => (siteUrl ? `${siteUrl}${route.path}` : undefined),
  ogImage: () =>
    siteUrl
      ? `${siteUrl}/home/gfs-group-tradelines-for-sale-800w.webp`
      : undefined,
  ogImageAlt: () => t("index_page.image_alt_content"),
  titleTemplate: () => "%s | GFS Group Tradeline Reviews",
});
</script>
